/**
 * RemotionPreview Component
 *
 * Renders a live Remotion video preview for product proposals.
 * Supports all 4 templates with real-time updates when props change.
 */

import React, { useMemo } from 'react';
import { Player } from '@remotion/player';

// Import templates
import { MinimalistReveal } from '../../remotion/templates/MinimalistReveal';
import { UrgencyFlashSale } from '../../remotion/templates/UrgencyFlashSale';
import { SocialProofShowcase } from '../../remotion/templates/SocialProofShowcase';
import { FeatureDemoFlow } from '../../remotion/templates/FeatureDemoFlow';

// Import types
import type {
  TemplateId,
  VisualEffectsConfig,
  BackgroundType,
  GradientPalette,
  TextAnimationType,
  CaptionFontId,
  MinimalistRevealProps,
  UrgencyFlashSaleProps,
  SocialProofShowcaseProps,
  FeatureDemoFlowProps,
} from '../../remotion/types';
import { DEFAULT_VIDEO_CONFIG, DEFAULT_VISUAL_EFFECTS } from '../../remotion/types';

/**
 * Convert snake_case keys to camelCase
 * Backend sends snake_case, templates expect camelCase
 */
function snakeToCamel(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    result[camelKey] = obj[key];
  }

  // Map backend field names to template field names
  // Backend uses product_image_url, templates expect productImage
  if (result.productImageUrl && !result.productImage) {
    result.productImage = result.productImageUrl;
  }
  // Map brand_primary_color to brandColor
  if (result.brandPrimaryColor && !result.brandColor) {
    result.brandColor = result.brandPrimaryColor;
  }

  return result;
}

// Template component mapping
const TEMPLATE_COMPONENTS: Record<TemplateId, React.ComponentType<any>> = {
  MinimalistReveal,
  UrgencyFlashSale,
  SocialProofShowcase,
  FeatureDemoFlow,
};

export interface RemotionPreviewProps {
  /** Template ID to render */
  templateId: TemplateId;
  /** Template variables from the proposal */
  templateVariables: Record<string, any>;
  /** Caption text (LLM-generated or user-edited) */
  captionText?: string;
  /** Caption color */
  captionColor?: string;
  /** Caption font ID */
  captionFont?: CaptionFontId;
  /** Visual effects configuration */
  effects?: VisualEffectsConfig;
  /** Background type: 'aurora' or 'animated_gradient' */
  backgroundType?: BackgroundType;
  /** Color palette for animated_gradient background */
  backgroundPalette?: GradientPalette;
  /** Text animation type */
  textAnimation?: TextAnimationType;
  /** Language code for CTA texts */
  language?: string;
  /** Show PAngV price disclaimer */
  showPriceDisclaimer?: boolean;
  /** Container width (optional - use className/style for responsive sizing) */
  width?: number;
  /** Container height (optional - use className/style for responsive sizing) */
  height?: number;
  /** Enable player controls */
  controls?: boolean;
  /** Autoplay on mount */
  autoPlay?: boolean;
  /** Loop playback */
  loop?: boolean;
  /**
   * Initial frame to show (for static preview mode)
   * Default: 0 (start of video)
   * Use 120 for second 4 at 30fps
   */
  initialFrame?: number;
  /** Custom className */
  className?: string;
  /** Custom inline styles (for responsive sizing) */
  style?: React.CSSProperties;
}

export const RemotionPreview: React.FC<RemotionPreviewProps> = ({
  templateId,
  templateVariables,
  captionText,
  captionColor = '#ffffff',
  captionFont = 'Modern',
  effects = DEFAULT_VISUAL_EFFECTS,
  backgroundType = 'aurora',
  backgroundPalette = 'aurora',
  textAnimation = 'typewriter',
  language,
  showPriceDisclaimer,
  width,
  height,
  controls = true,
  autoPlay = false,
  loop = true,
  initialFrame = 0,
  className = '',
  style,
}) => {
  // Get the template component
  const TemplateComponent = TEMPLATE_COMPONENTS[templateId];

  // Merge all props for the template
  // Convert snake_case keys from backend to camelCase for templates
  const inputProps = useMemo(() => {
    const converted = templateVariables ? snakeToCamel(templateVariables) : {};
    return {
      ...converted,
      captionText,
      captionColor,
      captionFont,
      effects,
      backgroundType,
      backgroundPalette,
      textAnimation,
      language,
      showPriceDisclaimer,
    };
  }, [templateVariables, captionText, captionColor, captionFont, effects, backgroundType, backgroundPalette, textAnimation, language, showPriceDisclaimer]);

  // Check if required props are present
  const hasRequiredProps = inputProps.productImage && inputProps.productTitle;

  // Build container styles - use explicit width/height if provided, otherwise use style prop
  const containerStyle: React.CSSProperties = {
    ...(width !== undefined && { width }),
    ...(height !== undefined && { height }),
    ...style,
  };

  if (!TemplateComponent) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-900 text-white ${className}`}
        style={containerStyle}
      >
        <div className="text-center p-4">
          <p className="text-sm text-gray-400">Unknown template:</p>
          <p className="font-mono text-xs">{templateId}</p>
        </div>
      </div>
    );
  }

  // Show fallback if required props are missing
  if (!hasRequiredProps) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-900 text-white ${className}`}
        style={containerStyle}
      >
        <div className="text-center p-4">
          <p className="text-sm text-gray-400">Missing required props:</p>
          <p className="font-mono text-xs mt-2">
            productImage: {inputProps.productImage ? '✓' : '✗'}<br/>
            productTitle: {inputProps.productTitle ? '✓' : '✗'}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Check console for details
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`remotion-preview ${className}`} style={containerStyle}>
      <Player
        component={TemplateComponent}
        inputProps={inputProps}
        durationInFrames={DEFAULT_VIDEO_CONFIG.durationInFrames}
        fps={DEFAULT_VIDEO_CONFIG.fps}
        compositionWidth={DEFAULT_VIDEO_CONFIG.width}
        compositionHeight={DEFAULT_VIDEO_CONFIG.height}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
        controls={controls}
        autoPlay={autoPlay}
        loop={loop}
        initialFrame={initialFrame}
        // Render settings
        renderLoading={() => (
          <div className="flex items-center justify-center w-full h-full bg-gray-900">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        )}
      />
    </div>
  );
};

export default RemotionPreview;
